import React, { useLayoutEffect, useRef, useState } from 'react';
import { applyMotionPlaybackRate, DEFAULT_MOTION_PLAYBACK_RATE } from './motionPlayback';

const mediaKey = (task) => `${task.id}|${task.videoUrl}`;

// Keep the actual decoders mounted: a cached MP4 alone still needs a new
// decoder/first frame when put in a freshly mounted <video> on iOS.
export default function MotionVideoDeck({ task, upcoming = [], bindVideo, videoProps, playbackRate = DEFAULT_MOTION_PLAYBACK_RATE, children }) {
  const nodes = useRef(new Map());
  const bindings = useRef(new Map());
  const order = useRef([]);
  const activeKey = mediaKey(task);
  const lastPresented = useRef(null);
  const [presentedKey, setPresentedKey] = useState(null);
  const latest = useRef({ activeKey, task, videoProps, playbackRate });
  latest.current = { activeKey, task, videoProps, playbackRate };
  const previous = lastPresented.current;
  // Five clips can be cached without allocating five extra native decoders.
  const available = new Map([task, ...upcoming.slice(0, 2), ...(previous ? [previous] : [])].map((item) => [mediaKey(item), item]));
  order.current = [...order.current.filter((key) => available.has(key)), ...[...available.keys()].filter((key) => !order.current.includes(key))];
  const items = order.current.map((key) => available.get(key));
  const ready = (key, element) => {
    if (key !== latest.current.activeKey) return;
    lastPresented.current = latest.current.task;
    setPresentedKey(key);
    latest.current.videoProps.onLoadedData?.({ currentTarget: element });
  };
  const binding = (key) => {
    if (!bindings.current.has(key)) bindings.current.set(key, (element) => {
      if (element) {
        element.defaultMuted = true; element.muted = true; element.playsInline = true;
        element.setAttribute('webkit-playsinline', '');
        applyMotionPlaybackRate(element, latest.current.playbackRate);
        nodes.current.set(key, element);
      } else {
        const old = nodes.current.get(key);
        old?.pause();
        old?.removeAttribute('src');
        nodes.current.delete(key);
        bindings.current.delete(key);
      }
    });
    return bindings.current.get(key);
  };
  useLayoutEffect(() => {
    for (const element of nodes.current.values()) applyMotionPlaybackRate(element, playbackRate);
  }, [playbackRate]);
  useLayoutEffect(() => {
    const element = nodes.current.get(activeKey);
    applyMotionPlaybackRate(element, latest.current.playbackRate);
    bindVideo(element || null);
    for (const [key, node] of nodes.current) if (key !== activeKey && !node.paused) node.pause();
    // loadeddata may have fired while this was an upcoming clip. Notify the
    // review screen now, before paint, without changing src or reloading it.
    if (element?.readyState >= 2) ready(activeKey, element);
    else if (element?.error) latest.current.videoProps.onError?.({ currentTarget: element });
    return () => bindVideo(null);
    // Callbacks are read through latest so ordinary UI updates cannot restart a video.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, bindVideo]);

  return <>{items.map((item) => {
    const key = mediaKey(item);
    const active = key === activeKey;
    // Retain the last rendered frame if connectivity beats the warm buffer.
    // Answer buttons stay disabled until the incoming clip has its own frame.
    const visible = key === presentedKey || (active && !presentedKey);
    return <div key={key} className={`cr-motion-deck-frame${visible ? ' is-presented' : ''}`}>
      <video {...(active ? videoProps : {})} ref={binding(key)} src={item.videoUrl}
      className={`cr-motion-deck-video${visible ? ' is-presented' : ''}`}
      aria-label={active ? 'Motion review clip' : undefined} aria-hidden={active ? undefined : true}
      tabIndex={active ? undefined : -1} data-preloaded-motion={active ? undefined : item.id}
      autoPlay={active && videoProps.autoPlay} controls={active && videoProps.controls}
      muted playsInline loop preload="auto" disablePictureInPicture
      onLoadedMetadata={(event) => {
        applyMotionPlaybackRate(event.currentTarget, latest.current.playbackRate);
        if (key === latest.current.activeKey) latest.current.videoProps.onLoadedMetadata?.(event);
      }}
      onLoadedData={(event) => ready(key, event.currentTarget)} />
      {active && children}
    </div>;
  })}</>;
}
